import { BigNumber } from '@ethersproject/bignumber';
import { markRaw } from 'vue';
import { ManifoldBridgeProvider } from '@manifoldxyz/manifold-provider-client';
import { Contract } from '@ethersproject/contracts';

export default class BaseContract {
  // Manifold bridge provider instance
  private manifoldBridgeProvider: ManifoldBridgeProvider | undefined;

  protected _getContractInstance(
    address: string,
    abi: any[], // eslint-disable-line
    withSigner = false,
    bridge = false,
    unchecked = false,
  ): Contract {
    if (bridge) return new Contract(address, abi, this._getManifoldBridgeProvider());
    const contract = window.ManifoldEthereumProvider.contractInstance(
      address,
      abi,
      withSigner,
      unchecked,
    );
    if (!contract)
      throw new Error('No contract instance available, please refresh this page to try again');
    return contract;
  }

  /**
   * Get the manifold bridge provider instance
   */
  private _getManifoldBridgeProvider(): ManifoldBridgeProvider {
    if (!this.manifoldBridgeProvider) {
      this.manifoldBridgeProvider = markRaw(
        new ManifoldBridgeProvider(window.ManifoldEthereumProvider.network() || 1), // @TODO FALLBACK tO PROP
      );
    }
    return this.manifoldBridgeProvider;
  }

  async _callWeb3WithServerFallback(
    address: string,
    abi: any[], // eslint-disable-line
    functionName: string,
    args: any[], // eslint-disable-line
    signer: boolean,
  ): Promise<any> { // eslint-disable-line
    const provider = window.ManifoldEthereumProvider.provider();
    if (!provider) {
      // No available provider failure scenario, use the server endpoint
      return this._getContractInstance(address, abi, signer, true)[functionName](...args);
    }

    try {
      // We have a web3timeout race because there are certain situations where
      // web3 requests will hang.  e.g. Safari websockets or Infura rate limiting
      // ref: https://developer.apple.com/forums/thread/679576
      // res: https://github.com/tilt-dev/tilt/issues/4746

      const web3timeout = new Promise((resolve) => setTimeout(resolve, 1500));
      const web3result = new Promise((resolve) => {
        try {
          resolve(this._getContractInstance(address, abi, signer)[functionName](...args));
        } catch {
          resolve(undefined);
        }
      });
      // eslint-disable-next-line
      let result: any = await Promise.race([web3timeout, web3result]);
      if (result === undefined) {
        // Fallback provider failure scenario, use the server endpoint
        result = await this._getContractInstance(address, abi, signer, true)[functionName](...args);
      }
      return result;
    } catch (e) {
      // try getting from server instead
      const result = await this._getContractInstance(address, abi, signer, true)[functionName](
        ...args,
      );

      return result;
    }
  }

  async _estimateGas3WithServerFallback(
    address: string,
    abi: any[], // eslint-disable-line
    functionSig: string,
    args: any[], // eslint-disable-line
  ): Promise<BigNumber> {
    if (!window.ManifoldEthereumProvider.chainIsCorrect()) throw new Error('Wrong Network');
    let gasEstimate;
    try {
      gasEstimate = await this._getContractInstance(address, abi, true).estimateGas[functionSig](
        ...args,
      );
    } catch (e) {
      // get etimate from manifold bridge instead
      gasEstimate = await this._getContractInstance(address, abi, true, true).estimateGas[
        functionSig
      ](...args);
    }

    // Multiply gas estimate by 1.25 to account for inaccurate estimates from Metamask.
    gasEstimate = gasEstimate.mul((1 + 0.25) * 100).div(100);
    return gasEstimate;
  }
}
